import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  approveBatch,
  bulkBatchDecision,
  getBatch,
  listBatches,
  previewBatch,
  rejectBatch,
  type Batch,
  type BulkBatchDecisionResponse,
} from "@/api/client";
import { ApprovalTabs } from "@/components/ApprovalTabs";
import { batchLabel } from "@/components/batchLabel";
import { BatchStats, BulkResultSummary, PreviewTable, ProgressBar } from "@/components/batch";
import { DataTable, type Column } from "@/components/DataTable";
import { PagedFooter } from "@/components/Pagination";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, StatusBadge, StatusDot, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { useRevealPanel } from "@/components/useRevealPanel";
import { pageParams } from "@/routes/paging";

const approvalsApi = getRouteApi("/staff/approvals");

export function ApprovalsWorkspace() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = approvalsApi.useSearch();
  const { page, page_size } = search;
  const activeBatchID = search.batch ?? null;
  const setActiveBatchID = (batch: string | undefined) => void navigate({ to: "/approvals", search: { batch, page, page_size } });
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [result, setResult] = useState<BulkBatchDecisionResponse | null>(null);
  // Names captured at submit time: decided batches leave the queue when it refetches.
  const [resultNames, setResultNames] = useState<Record<string, string>>({});

  const batches = useQuery({
    queryKey: ["batches", "pending_approval", page, page_size],
    queryFn: () => listBatches({ status: "pending_approval", ...pageParams(search) }),
    refetchInterval: 15_000,
  });
  const items = batches.data?.items ?? [];

  const onPage = items.find((item) => item.id === activeBatchID) ?? null;
  // A linked batch may sit on another page, so look it up before calling it stale.
  const linked = useQuery({
    queryKey: ["batch", activeBatchID],
    queryFn: () => getBatch(activeBatchID!),
    enabled: activeBatchID !== null && batches.isSuccess && onPage === null,
  });
  const activeBatch = onPage ?? (linked.data?.status === "pending_approval" ? linked.data : null);
  // A linked batch that has left the queue (decided by someone else, or never pending).
  const stale = activeBatchID !== null && activeBatch === null && batches.isSuccess && !linked.isPending;
  const preview = useQuery({
    queryKey: ["batch-preview", activeBatchID],
    queryFn: () => previewBatch(activeBatchID!),
    enabled: activeBatch !== null,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["batches"] });
    await queryClient.invalidateQueries({ queryKey: ["batch-preview"] });
    await queryClient.invalidateQueries({ queryKey: ["batch-report"] });
  };

  const approveOne = useMutation({
    mutationFn: approveBatch,
    onSuccess: async () => {
      setSelected([]);
      setResult(null);
      setActiveBatchID(undefined);
      await invalidate();
    },
  });
  const rejectOne = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectBatch(id, note),
    onSuccess: async () => {
      setSelected([]);
      setComment("");
      setResult(null);
      setActiveBatchID(undefined);
      await invalidate();
    },
  });
  const bulk = useMutation({
    mutationFn: bulkBatchDecision,
    onSuccess: async (data) => {
      setResultNames(Object.fromEntries(items.map((item) => [item.id, batchLabel(item)])));
      setResult(data);
      setSelected([]);
      await invalidate();
    },
  });

  const busy = approveOne.isPending || rejectOne.isPending || bulk.isPending;
  const { panelRef, headingRef, reveal } = useRevealPanel(activeBatchID);
  const pick = (id: string) => {
    reveal(id);
    setActiveBatchID(id);
  };
  const selectedSet = new Set(selected);
  const toggle = (id: string, checked: boolean) =>
    setSelected(checked ? [...selected.filter((s) => s !== id), id] : selected.filter((s) => s !== id));

  // Bulk checkboxes are table-only: a checkbox cannot sit inside a card's selection button.
  const columns: Column<Batch>[] = [
    {
      key: "select",
      header: "",
      className: "w-10",
      hideOnCard: true,
      cell: (batch) => (
        // Covers the cell padding so a near miss never opens the batch.
        <label className="-mx-4 -my-3 flex px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Select ${batchLabel(batch)}`}
            checked={selectedSet.has(batch.id)}
            onChange={(e) => toggle(batch.id, e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300"
          />
        </label>
      ),
    },
    {
      key: "batch",
      header: "Batch",
      primary: true,
      cell: (batch) => (
        <>
          <span className="block font-medium text-zinc-950">{batchLabel(batch)}</span>
        </>
      ),
    },
    { key: "status", header: "Status", cell: (batch) => <StatusBadge status={batch.status} /> },
    {
      key: "recipients",
      header: "Recipients",
      cell: (batch) => <span className="text-zinc-700">{batch.total_count}</span>,
    },
    // ProgressBar is block content, which a card's selection button can't hold.
    { key: "progress", header: "Progress", hideOnCard: true, cell: (batch) => <ProgressBar batch={batch} /> },
    {
      key: "created",
      header: "Created",
      cell: (batch) => <span className="text-zinc-500">{formatDate(batch.created_at)}</span>,
    },
  ];
  const selectedIDs = selected;

  return (
    <section className="space-y-5">
      <ApprovalTabs />
      <WorkspaceHeader
        title="Approval queue"
        detail={`${batches.data?.total ?? 0} batch${batches.data?.total === 1 ? "" : "es"} pending approval`}
        action={<StatusDot label={batches.isFetching ? "Refreshing" : "Live"} />}
      />

      <ErrorBanner error={batches.error ?? approveOne.error ?? rejectOne.error ?? bulk.error} />
      {result ? <BulkResultSummary result={result} names={resultNames} /> : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] xl:gap-5">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
            <div className="text-sm font-medium">Pending batches</div>
            <div className="hidden flex-wrap items-center gap-2 md:flex">
              <ToolbarButton tone="primary"
                disabled={selectedIDs.length === 0 || busy}
                onClick={() => bulk.mutate({ action: "approve", batch_ids: selectedIDs })}
              >
                Approve selected
              </ToolbarButton>
              <ToolbarButton
                disabled={selectedIDs.length === 0 || busy || comment.trim() === ""}
                onClick={() => bulk.mutate({ action: "reject", batch_ids: selectedIDs, comment: comment.trim() })}
                tone="danger"
              >
                Reject selected
              </ToolbarButton>
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState title="No batches pending approval" detail="Validated batches appear here after submission." />
          ) : (
            <div className="p-3 md:p-0">
              <DataTable
                rows={items}
                columns={columns}
                rowKey={(item) => item.id}
                label="Pending batches"
                tableClassName="min-w-[760px]"
                onSelect={(item) => pick(item.id)}
                selectedKey={activeBatchID ?? undefined}
              />
            </div>
          )}
          <PagedFooter data={batches.data} page={page} pageSize={page_size} />
        </div>

        <aside ref={panelRef} className="space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 ref={headingRef} tabIndex={-1} className="text-sm font-semibold focus:outline-none">
                  Decision detail
                </h2>
                <p className="mt-1 text-xs text-zinc-500">{activeBatch ? batchLabel(activeBatch) : "No batch selected"}</p>
              </div>
              {activeBatch ? <StatusBadge status={activeBatch.status} /> : null}
            </div>

            {activeBatch ? (
              <div className="mt-4 space-y-4">
                <BatchStats batch={activeBatch} />
                <PreviewTable rows={preview.data ?? []} loading={preview.isLoading} />
                <label className="block text-sm">
                  <span className="font-medium text-zinc-700">Rejection comment</span>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    className="mt-1 w-full resize-none rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                  />
                </label>
                <div className="flex gap-2">
                  <ToolbarButton tone="primary" disabled={busy} onClick={() => approveOne.mutate(activeBatch.id)}>
                    Approve
                  </ToolbarButton>
                  <ToolbarButton
                    disabled={busy || comment.trim() === ""}
                    onClick={() => rejectOne.mutate({ id: activeBatch.id, note: comment.trim() })}
                    tone="danger"
                  >
                    Reject
                  </ToolbarButton>
                </div>
              </div>
            ) : stale ? (
              <div className="mt-4 space-y-3">
                <EmptyState
                  title="No longer pending approval"
                  detail="This batch has already been decided or was never submitted."
                  compact
                />
                <Link
                  to="/batches/$batchId"
                  params={{ batchId: activeBatchID }}
                  search={{ status: "all" }}
                  className="text-sm font-medium text-zinc-950 underline"
                >
                  Open in batch monitor
                </Link>
              </div>
            ) : (
              <EmptyState title="Select a batch" detail="Preview rows and decisions stay in this panel." compact />
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
